import { createFileRoute, Link } from "@tanstack/react-router";
import { EditablePageShell, useSitePage } from "@/components/site/EditablePageShell";
import { pageQueryOptions } from "@/lib/pages.functions";
import sixPackGlow from "@/assets/product-six-pack-glow.jpg";
import { SHARE_IMAGES } from "@/lib/share-images";

export const Route = createFileRoute("/how-it-works")({
  loader: ({ context }) => context.queryClient.ensureQueryData(pageQueryOptions("how-it-works")),
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.title || "How Glow Golf Balls Work — Glow Long Golf" },
      { name: "description", content: loaderData?.meta_description || "How easy activation, glow duration, and night-play work for Glow Long golf balls." },
      { property: "og:title", content: loaderData?.title || "How Glow Golf Balls Work — Glow Long Golf" },
      { property: "og:description", content: loaderData?.meta_description || "How easy activation, glow duration, and night-play work for Glow Long golf balls." },
      { property: "og:url", content: "https://glowlonggolf.com/how-it-works" },
      { property: "og:image", content: SHARE_IMAGES.teeOff },
      { name: "twitter:image", content: SHARE_IMAGES.teeOff },
    ],
    links: [{ rel: "canonical", href: "https://glowlonggolf.com/how-it-works" }],
  }),

  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive" role="alert">{error.message}</div>,
  notFoundComponent: () => <div className="p-8 text-sm text-muted-foreground">Not found.</div>,
  component: HowItWorks,
});

const DEFAULT_STEPS = [
  { title: "Easy Activation", body: "To activate the ball, simply drop it onto a hard surface. The built-in impact sensor turns the glow on automatically, so it's ready to play right away." },
  { title: "Tee up", body: "Place your glowing ball on a tee or the grass. They play like a standard 2-piece ball." },
  { title: "Bounce It Back to Life", body: "The ball automatically turns off after 10 minutes of no impact. A quick bounce on a hard surface turns the glow right back on." },
];

function HowItWorks() {
  const page = useSitePage("how-it-works");
  const extraSteps = Array.isArray(page?.extra?.how_it_works_steps) ? page!.extra.how_it_works_steps : null;
  const steps = (extraSteps && extraSteps.length === 3 ? extraSteps : DEFAULT_STEPS);
  const heroImg = page?.hero_image_url || sixPackGlow;

  return (
    <EditablePageShell
      slug="how-it-works"
      defaults={{ eyebrow: "How it works", title: "Three steps to glow", lead: "Drop it, tee it up, and bounce it back to life. No batteries, no switches." }}
    >
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-12 sm:px-6 md:grid-cols-2 lg:px-8">
        <div className="overflow-hidden rounded-3xl border border-border bg-card">
          <img src={heroImg} alt="Six glowing golf balls" className="h-full w-full object-cover" loading="lazy" />
        </div>
        <ol className="space-y-6">
          {steps.map((s: any, i: number) => (
            <li key={i} className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-semibold">{i + 1} — {s.title}</h2>
              <p className="mt-2 text-muted-foreground">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>
      <section className="mx-auto max-w-4xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-border bg-card/40 p-8 text-sm text-muted-foreground">
          <h2 className="font-display text-xl font-semibold text-foreground">Care & safety</h2>
          <p className="mt-3">Use only in open areas. Never aim toward people, animals, vehicles, roads, windows, or property. Glow Long is a recreational product — not officially tournament approved. Glow duration and brightness will vary based on activation surface, drop height, and conditions.</p>
        </div>
        <div className="mt-8 text-center">
          <Link to="/shop" className="inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">Shop the 6-pack</Link>
        </div>
      </section>
    </EditablePageShell>
  );
}
